import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, fromEvent, Observable } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { AsyncPipe, NgIf } from '@angular/common';
import { isBoolean } from 'lodash-es';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { SingleValueFormInput } from '../../../../helpers/single-value-form-input';
import { ShIndeterminateDirective } from '../../../directives/indeterminate.directive';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';

@Component({
    selector: 'sh-form-four-state-checkbox-complex-base',
    templateUrl: './form-four-state-checkbox-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormFourStateCheckboxComplexBaseComponent)],
    standalone: true,
    imports: [ReactiveFormsModule, ShIndeterminateDirective, NgIf, AsyncPipe]
})
export class ShFormFourStateCheckboxComplexBaseComponent
    extends SingleValueFormInput<boolean | null>
    implements OnInit, OnDestroy, AfterViewInit
{
    public readonly unchecked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public readonly indeterminate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    control: UntypedFormControl;

    @Input() id!: string;

    @Input() invalid = false;

    @Input() describeLabelId = '';

    @Input() label = '';

    @ViewChild('checkboxEl') checkboxEl!: ElementRef;

    constructor(changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);

        this.control = new UntypedFormControl();
    }

    ngOnInit(): void {
        this.initValueListener();
        this.initPropagation();
    }

    ngAfterViewInit(): void {
        this.initCheckboxListener();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.indeterminate$.complete();
    }

    private initValueListener(): void {
        this.control.valueChanges
            .pipe(
                filter(value => value !== null),
                takeUntil(this.destroy$)
            )
            .subscribe(value => this.unchecked$.next(!value));
    }

    initPropagation(): void {
        combineLatest([this.control.valueChanges, this.indeterminate$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([value, indeterminate]) => {
                if (indeterminate) {
                    return this.propagateChange(new FormValue(false, FormValueState.INDETERMINATE));
                }

                if (value !== null) {
                    return this.propagateChange(new FormValue(value, FormValueState.SET));
                } else {
                    return this.propagateChange(new FormValue(null, FormValueState.UNSET));
                }
            });
    }

    initCheckboxListener(): void {
        const changes$: Observable<any> = fromEvent(this.checkboxEl.nativeElement, 'change').pipe();

        const firstChange$: Observable<any> = changes$.pipe(
            filter(value => !!value),
            take(1)
        );

        firstChange$.pipe(takeUntil(this.destroy$)).subscribe(() => this.indeterminate$.next(false));
    }

    public unset(): void {
        this.indeterminate$.next(false);
        this.unchecked$.next(false);

        this.control.setValue(null);
    }

    public set(value: boolean | null): void {
        if (!isBoolean(value) || value === null) {
            console.error('ShFormFourStateCheckboxComplexBaseComponent:: FormValue does not contain a boolean', value);

            this.unset();

            return;
        }

        this.indeterminate$.next(false);
        this.unchecked$.next(value === false);

        this.control.setValue(value);
    }

    public makeIndeterminate(): void {
        this.indeterminate$.next(true);
        this.unchecked$.next(false);

        this.control.setValue(null);
    }
}
